import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import styles from './Macro.module.css';
import RecorrenciaTab from './RecorrenciaTab';

// Helpers de filtro/sort por coluna
const normalize = (s) => String(s ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

function applyFilters(rows, filters) {
  return rows.filter(row => {
    for (const [key, cfg] of Object.entries(filters)) {
      const valor = row[key];
      if (cfg.type === 'text' && cfg.value) {
        if (!normalize(valor).includes(normalize(cfg.value))) return false;
      } else if (cfg.type === 'select' && cfg.value) {
        if (String(valor) !== String(cfg.value)) return false;
      } else if (cfg.type === 'number') {
        const n = Number(valor);
        if (cfg.min !== '' && cfg.min !== undefined && !isNaN(Number(cfg.min)) && n < Number(cfg.min)) return false;
        if (cfg.max !== '' && cfg.max !== undefined && !isNaN(Number(cfg.max)) && n > Number(cfg.max)) return false;
      } else if (cfg.type === 'date') {
        const d = valor ? String(valor).slice(0, 10) : '';
        if (cfg.from && d && d < cfg.from) return false;
        if (cfg.to && d && d > cfg.to) return false;
        if ((cfg.from || cfg.to) && !d) return false;
      }
    }
    return true;
  });
}

function applySort(rows, sortKey, sortDir) {
  if (!sortKey) return rows;
  const dir = sortDir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const va = a[sortKey];
    const vb = b[sortKey];
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    const na = Number(va);
    const nb = Number(vb);
    if (!isNaN(na) && !isNaN(nb)) return (na - nb) * dir;
    return String(va).localeCompare(String(vb), 'pt-BR') * dir;
  });
}

// Componente de filtro inline para a célula do header
function ColumnFilter({ filter, onChange, options }) {
  if (!filter) return null;
  const baseStyle = {
    width: '100%',
    fontSize: '0.72rem',
    padding: '0.2rem 0.35rem',
    border: '1px solid #DEE2E6',
    borderRadius: 4,
    background: '#fff',
  };
  if (filter.type === 'text') {
    return (
      <input
        type="text"
        placeholder="filtrar..."
        value={filter.value || ''}
        onChange={(e) => onChange({ ...filter, value: e.target.value })}
        style={baseStyle}
      />
    );
  }
  if (filter.type === 'select') {
    return (
      <select
        value={filter.value || ''}
        onChange={(e) => onChange({ ...filter, value: e.target.value })}
        style={baseStyle}
      >
        <option value="">Todos</option>
        {(options || []).map(o => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    );
  }
  if (filter.type === 'number') {
    return (
      <div style={{ display: 'flex', gap: 4 }}>
        <input
          type="number"
          placeholder="min"
          value={filter.min ?? ''}
          onChange={(e) => onChange({ ...filter, min: e.target.value })}
          style={{ ...baseStyle, width: '50%' }}
        />
        <input
          type="number"
          placeholder="max"
          value={filter.max ?? ''}
          onChange={(e) => onChange({ ...filter, max: e.target.value })}
          style={{ ...baseStyle, width: '50%' }}
        />
      </div>
    );
  }
  if (filter.type === 'date') {
    return (
      <div style={{ display: 'flex', gap: 4 }}>
        <input
          type="date"
          value={filter.from || ''}
          onChange={(e) => onChange({ ...filter, from: e.target.value })}
          style={{ ...baseStyle, width: '50%' }}
        />
        <input
          type="date"
          value={filter.to || ''}
          onChange={(e) => onChange({ ...filter, to: e.target.value })}
          style={{ ...baseStyle, width: '50%' }}
        />
      </div>
    );
  }
  return null;
}

// Header clicável com indicador de sort
function SortableTh({ children, sortKey, currentSort, onSort, align }) {
  if (!sortKey) {
    return <th style={align ? { textAlign: align } : undefined}>{children}</th>;
  }
  const active = currentSort.key === sortKey;
  const arrow = active ? (currentSort.dir === 'desc' ? '▼' : '▲') : '⇅';
  return (
    <th
      style={{ cursor: 'pointer', userSelect: 'none', textAlign: align || undefined }}
      onClick={() => onSort(sortKey)}
      title="Clique para ordenar"
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {children}
        <span style={{ fontSize: '0.7rem', opacity: active ? 1 : 0.35 }}>{arrow}</span>
      </span>
    </th>
  );
}

const fmtBRL = (v) => Number(v || 0).toLocaleString('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const fmtNum = (v) => Number(v || 0).toLocaleString('pt-BR');

// Formata data/hora ou só data:
//  - "2026-05-29" → "29/05/2026" (parse local, evita UTC virar dia anterior em BRT)
//  - "2026-05-29T15:30:00Z" → "29/05/2026, 12:30:00" (timestamp completo, com hora)
const fmtDate = (s) => {
  if (!s) return '-';
  if (typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('pt-BR');
  }
  return new Date(s).toLocaleString('pt-BR');
};

const STATUS_COLORS = {
  'Enviado': '#8E44AD',
  'Entregue e Finalizado': '#2ECC71',
  'Pagamento Confirmado': '#3498DB',
  'Aguardando Pagamento': '#F39C12',
  'Cancelado': '#E74C3C',
  'Em Produção': '#F1C40F',
  'Em Análise': '#9CCC65',
  'Aguardando para Retirada': '#9575CD',
};

const statusClass = (s) => ({
  'Enviado': styles.statusEnviado,
  'Entregue e Finalizado': styles.statusEntregue,
  'Pagamento Confirmado': styles.statusPago,
  'Aguardando Pagamento': styles.statusAguardando,
  'Cancelado': styles.statusCancelado,
  'Em Produção': styles.statusProducao,
  'Em Análise': styles.statusAnalise,
  'Aguardando para Retirada': styles.statusRetirada,
}[s] || styles.statusEnviado);

// Período padrão: últimos 30 dias
const hoje = new Date().toISOString().slice(0, 10);
const trintaDias = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);

// Tooltip customizada do Top 10 — mostra imagem, descrição, qtd e receita.
const TopProdutosTooltip = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0].payload;
  const img = d.foto_url_thumb || d.foto_url;
  return (
    <div style={{
      background: 'white',
      padding: '10px 12px',
      border: '1px solid #E9ECEF',
      borderRadius: '6px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      maxWidth: 260,
      fontSize: '0.85rem'
    }}>
      {img && (
        <img
          src={img}
          alt={d.referencia}
          loading="lazy"
          style={{
            width: '100%',
            maxHeight: 160,
            objectFit: 'contain',
            display: 'block',
            marginBottom: 8,
            borderRadius: 4,
            background: '#F8F9FA'
          }}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      )}
      <div style={{ fontWeight: 700, color: '#212529', marginBottom: 2 }}>{d.referencia}</div>
      {(d.produto_nome || d.descricao) && (
        <div style={{ color: '#495057', fontSize: '0.78rem', marginBottom: 6, lineHeight: 1.3 }}>
          {d.produto_nome || d.descricao}
        </div>
      )}
      <div style={{ color: '#6C5CE7', fontWeight: 600 }}>
        Qtd Vendida: {new Intl.NumberFormat('pt-BR').format(d.total_vendido || 0)}
      </div>
      {d.receita !== undefined && (
        <div style={{ color: '#2ECC71', fontWeight: 600, fontSize: '0.78rem' }}>
          Receita: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(d.receita || 0)}
        </div>
      )}
      {d.pedidos !== undefined && (
        <div style={{ color: '#6C757D', fontSize: '0.75rem' }}>
          {d.pedidos} pedido{d.pedidos === 1 ? '' : 's'}
        </div>
      )}
    </div>
  );
};

const Macro = () => {
  const [tab, setTab] = useState('overview');
  // Default: HOJE (intervalo de 1 dia). Usuário pode expandir pra um período.
  // Os KPIs do topo refletem exatamente o intervalo selecionado.
  const [dataInicio, setDataInicio] = useState(hoje);
  const [dataFim, setDataFim] = useState(hoje);
  const [filtroStatus, setFiltroStatus] = useState('');

  const [kpis, setKpis] = useState(null);
  const [porStatus, setPorStatus] = useState([]);
  const [diario, setDiario] = useState([]);
  // Série separada pro gráfico "Faturamento por Dia": SEMPRE os últimos 20 dias do dia atual,
  // independente do filtro de período acima. Carrega 1x junto com loadData.
  const [diarioGrafico, setDiarioGrafico] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [topProdutos, setTopProdutos] = useState([]);
  // "Pedidos por Status" e "Top 10 Produtos": SEMPRE mês atual, independente do filtro.
  const [porStatusMes, setPorStatusMes] = useState([]);
  const [topProdutosMes, setTopProdutosMes] = useState([]);
  const [cestas, setCestas] = useState([]);
  const [clientes, setClientes] = useState([]);

  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [modalCesta, setModalCesta] = useState(null); // { loading, cliente, cesta, itens }
  const [modalPedido, setModalPedido] = useState(null); // { loading, pedido, itens, cliente, error }
  const [modalKpi, setModalKpi] = useState(null); // { tipo: 'faturamento'|'ticket_medio'|'faturamento_dia'|'cancelados'|'novos_clientes'|'cestas'|'clientes'|'descontos' }

  // Filtros por coluna (por tabela) + sort
  const [cestaFilters, setCestaFilters] = useState({
    nome: { type: 'text', value: '' },
    email: { type: 'text', value: '' },
    qtde_itens: { type: 'number', min: '', max: '' },
    dias_abandono: { type: 'number', min: '', max: '' },
    data_inclusao_mais_antiga: { type: 'date', from: '', to: '' },
    valor_estimado: { type: 'number', min: '', max: '' },
  });
  const [cestaSort, setCestaSort] = useState({ key: 'valor_estimado', dir: 'desc' });

  const [pedidoFilters, setPedidoFilters] = useState({
    id: { type: 'text', value: '' },
    data: { type: 'date', from: '', to: '' },
    cliente_nome: { type: 'text', value: '' },
    situacao: { type: 'select', value: '' },
    tipo_pagamento: { type: 'select', value: '' },
    qtde_itens: { type: 'number', min: '', max: '' },
    frete_descricao: { type: 'select', value: '' },
    valor_total: { type: 'number', min: '', max: '' },
  });
  const [pedidoSort, setPedidoSort] = useState({ key: 'data', dir: 'desc' });

  const [clienteFilters, setClienteFilters] = useState({
    nome: { type: 'text', value: '' },
    email: { type: 'text', value: '' },
    documento: { type: 'text', value: '' },
    perfil: { type: 'select', value: '' },
    minimo_valor: { type: 'number', min: '', max: '' },
    desconto: { type: 'number', min: '', max: '' },
  });
  const [clienteSort, setClienteSort] = useState({ key: 'nome', dir: 'asc' });

  const api = useCallback(async (path, params = {}) => {
    const { data } = await axios.get(`/api/macro${path}`, { params });
    return data;
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { data_inicio: dataInicio, data_fim: dataFim };
      // Gráfico Faturamento por Dia: janela fixa de 20 dias terminando hoje,
      // independente do filtro acima.
      const vinteDiasAtras = new Date(Date.now() - 20 * 86400000).toISOString().slice(0, 10);
      const paramsGrafico = { data_inicio: vinteDiasAtras, data_fim: hoje };
      // "Pedidos por Status" e "Top 10 Produtos": SEMPRE mês atual.
      const inicioMes = new Date();
      inicioMes.setDate(1);
      const paramsMes = { data_inicio: inicioMes.toISOString().slice(0, 10), data_fim: hoje };
      const [k, d, p, t, c, cl, dGraf, kMes, tMes] = await Promise.all([
        api('/kpis', params),
        api('/faturamento-diario', params),
        api('/pedidos', { ...params, status: filtroStatus, por_pagina: 1000 }),
        api('/top-produtos', params),
        api('/cestas'),
        api('/clientes', { por_pagina: 1000 }),
        api('/faturamento-diario', paramsGrafico),
        api('/kpis', paramsMes),
        api('/top-produtos', paramsMes),
      ]);
      setKpis(k.kpis);
      setPorStatus(k.por_status || []);
      setDiario(d.data || []);
      setPedidos(p.data || []);
      setTopProdutos(t.data || []);
      setCestas(c.data || []);
      setClientes(cl.data || []);
      setDiarioGrafico(dGraf.data || []);
      setPorStatusMes(kMes.por_status || []);
      setTopProdutosMes(tMes.data || []);
    } catch (err) {
      console.error('Erro ao carregar dados Macro:', err);
    } finally {
      setLoading(false);
    }
  }, [api, dataInicio, dataFim, filtroStatus]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Aplicar filtros + sort para cada tabela
  const cestasFiltered = useMemo(() => {
    return applySort(applyFilters(cestas, cestaFilters), cestaSort.key, cestaSort.dir);
  }, [cestas, cestaFilters, cestaSort]);

  const pedidosFiltered = useMemo(() => {
    return applySort(applyFilters(pedidos, pedidoFilters), pedidoSort.key, pedidoSort.dir);
  }, [pedidos, pedidoFilters, pedidoSort]);

  const clientesFiltered = useMemo(() => {
    // Adicionar campo virtual 'documento' (cpf || cnpj) para filtro
    const enriched = clientes.map(c => ({ ...c, documento: c.cpf || c.cnpj || '' }));
    return applySort(applyFilters(enriched, clienteFilters), clienteSort.key, clienteSort.dir);
  }, [clientes, clienteFilters, clienteSort]);

  // Opções únicas para selects (calculadas dos dados)
  const pedidoStatusOptions = useMemo(
    () => [...new Set(pedidos.map(p => p.situacao).filter(Boolean))].sort(),
    [pedidos]
  );
  const pedidoPagamentoOptions = useMemo(
    () => [...new Set(pedidos.map(p => p.tipo_pagamento).filter(Boolean))].sort(),
    [pedidos]
  );
  const pedidoFreteOptions = useMemo(
    () => [...new Set(pedidos.map(p => p.frete_descricao).filter(Boolean))].sort(),
    [pedidos]
  );
  const clientePerfilOptions = useMemo(
    () => [...new Set(clientes.map(c => c.perfil).filter(Boolean))].sort(),
    [clientes]
  );

  // Helpers para sort/reset
  const toggleSort = (current, setSort, key) => {
    if (current.key !== key) return setSort({ key, dir: 'asc' });
    if (current.dir === 'asc') return setSort({ key, dir: 'desc' });
    return setSort({ key: null, dir: null });
  };
  const updateFilter = (filters, setFilters, key) => (newCfg) => {
    setFilters({ ...filters, [key]: newCfg });
  };
  const limparFiltros = (filters, setFilters) => {
    const cleared = {};
    for (const [k, v] of Object.entries(filters)) {
      if (v.type === 'text' || v.type === 'select') cleared[k] = { ...v, value: '' };
      else if (v.type === 'number') cleared[k] = { ...v, min: '', max: '' };
      else if (v.type === 'date') cleared[k] = { ...v, from: '', to: '' };
    }
    setFilters(cleared);
  };
  const hasActiveFilter = (filters) => Object.values(filters).some(f =>
    (f.type === 'text' && f.value) ||
    (f.type === 'select' && f.value) ||
    (f.type === 'number' && (f.min || f.max)) ||
    (f.type === 'date' && (f.from || f.to))
  );

  const abrirItensCesta = useCallback(async (id_usuario) => {
    setModalCesta({ loading: true });
    try {
      const data = await api(`/cesta/${id_usuario}/itens`);
      setModalCesta({ loading: false, ...data });
    } catch (err) {
      console.error('Erro ao carregar itens:', err);
      setModalCesta({ loading: false, error: err.message });
    }
  }, [api]);

  const abrirPedido = useCallback(async (id) => {
    setModalPedido({ loading: true });
    try {
      const data = await api(`/pedido/${id}`);
      setModalPedido({ loading: false, ...data });
    } catch (err) {
      console.error('Erro ao carregar pedido:', err);
      setModalPedido({ loading: false, error: err.message });
    }
  }, [api]);

  // Faturamento do período selecionado:
  //  - Se dataInicio === dataFim (1 dia, default = HOJE): mostra o valor desse dia
  //  - Senão (intervalo): soma todos os dias do array diario
  // Label e sub mudam dinamicamente.
  const faturamentoDoDia = useMemo(() => {
    const ehUmDia = dataInicio === dataFim;
    if (!diario || diario.length === 0) {
      return { valor: 0, dia: ehUmDia ? dataInicio : null, pedidos: 0, ehUmDia, periodoInicio: dataInicio, periodoFim: dataFim };
    }
    if (ehUmDia) {
      const entry = diario.find(d => d.dia === dataInicio);
      return {
        valor: Number(entry?.faturamento) || 0,
        dia: dataInicio,
        pedidos: Number(entry?.pedidos) || 0,
        ehUmDia: true,
        periodoInicio: dataInicio,
        periodoFim: dataFim
      };
    }
    // Período: soma todos os dias retornados pelo backend (já filtrados pelo intervalo)
    const totalValor = diario.reduce((s, d) => s + (Number(d.faturamento) || 0), 0);
    const totalPedidos = diario.reduce((s, d) => s + (Number(d.pedidos) || 0), 0);
    return {
      valor: totalValor,
      dia: null,
      pedidos: totalPedidos,
      ehUmDia: false,
      periodoInicio: dataInicio,
      periodoFim: dataFim
    };
  }, [diario, dataInicio, dataFim]);

  const handleSync = async () => {
    if (!confirm('Sincronizar dados da eMacro agora? Pode levar até 1 minuto.')) return;
    setSyncing(true);
    try {
      const { data } = await axios.post(`/api/macro/sync`);
      if (data.success) {
        alert(`Sync concluído!\nPedidos: ${data.resultado.pedidos}\nClientes: ${data.resultado.clientes}\nCestas: ${data.resultado.cestas}`);
        await loadData();
      } else {
        alert('Erro no sync: ' + (data.error || 'desconhecido'));
      }
    } catch (err) {
      alert('Erro: ' + err.message);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className={styles.macroContainer}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Macro · E-commerce</h1>
          <div className={styles.subtitle}>
            Dados sincronizados da plataforma eMacro
            {kpis?.ultimo_sync && (
              <> · Último sync: {fmtDate(kpis.ultimo_sync)}</>
            )}
          </div>
        </div>
        <button className={styles.btnSync} onClick={handleSync} disabled={syncing}>
          {syncing ? 'Sincronizando...' : '🔄 Sincronizar agora'}
        </button>
      </div>

      <div className={styles.filterCard}>
        <div className={styles.filterGroup}>
          <label>Data início</label>
          <input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
        </div>
        <div className={styles.filterGroup}>
          <label>Data fim</label>
          <input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
        </div>
        <div className={styles.filterGroup}>
          <label>Status</label>
          <select value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
            <option value="">Todos</option>
            {porStatus.map(s => (
              <option key={s.situacao} value={s.situacao}>{s.situacao}</option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.tabs}>
        {['overview', 'pedidos', 'cestas', 'clientes', 'recorrencia'].map(t => (
          <button
            key={t}
            className={`${styles.tab} ${tab === t ? styles.tabActive : ''}`}
            onClick={() => setTab(t)}
          >
            {{ overview: 'Visão Geral', pedidos: 'Pedidos', cestas: 'Cestas Abandonadas', clientes: 'Clientes', recorrencia: '🔁 Recorrência' }[t]}
          </button>
        ))}
      </div>

      {loading && <div className={styles.loading}>Carregando...</div>}

      {!loading && tab === 'overview' && kpis && (
        <>
          <div className={styles.kpiGrid}>
            {/* PRIMEIRO card: Faturamento do Período. Default = HOJE; respeita filtro do usuário. */}
            <div className={`${styles.kpiCard} ${styles.success}`} onClick={() => setModalKpi({ tipo: 'faturamento_dia' })} style={{ cursor: 'pointer' }} title="Ver pedidos do período">
              <div className={styles.kpiLabel}>Faturamento do Período</div>
              <div className={styles.kpiValue}>{fmtBRL(faturamentoDoDia.valor)}</div>
              <div className={styles.kpiSub}>
                {faturamentoDoDia.ehUmDia
                  ? `${fmtDate(faturamentoDoDia.dia)} · ${fmtNum(faturamentoDoDia.pedidos)} pedidos`
                  : `${fmtDate(faturamentoDoDia.periodoInicio)} a ${fmtDate(faturamentoDoDia.periodoFim)} · ${fmtNum(faturamentoDoDia.pedidos)} pedidos`}
              </div>
            </div>
            <div className={styles.kpiCard} onClick={() => setModalKpi({ tipo: 'ticket_medio' })} style={{ cursor: 'pointer' }} title="Ver evolução do ticket">
              <div className={styles.kpiLabel}>Ticket Médio</div>
              <div className={styles.kpiValue}>{fmtBRL(kpis.ticket_medio)}</div>
            </div>
            <div className={`${styles.kpiCard} ${styles.danger}`} onClick={() => setModalKpi({ tipo: 'cancelados' })} style={{ cursor: 'pointer' }} title="Ver pedidos cancelados">
              <div className={styles.kpiLabel}>Cancelados</div>
              <div className={styles.kpiValue}>{fmtNum(kpis.cancelados)}</div>
            </div>
            <div className={`${styles.kpiCard} ${styles.info}`} onClick={() => setModalKpi({ tipo: 'novos_clientes' })} style={{ cursor: 'pointer' }} title="Ver novos clientes">
              <div className={styles.kpiLabel}>Novos Clientes</div>
              <div className={styles.kpiValue}>{fmtNum(kpis.novos_clientes)}</div>
              <div className={styles.kpiSub}>1º pedido no período</div>
            </div>
            <div className={`${styles.kpiCard} ${styles.warning}`} onClick={() => setModalKpi({ tipo: 'cestas' })} style={{ cursor: 'pointer' }} title="Ver cestas abandonadas">
              <div className={styles.kpiLabel}>Cestas Abandonadas</div>
              <div className={styles.kpiValue}>{fmtNum(kpis.cestas_abandonadas)}</div>
              <div className={styles.kpiSub}>{fmtBRL(kpis.valor_cestas)} em aberto</div>
            </div>
            <div className={styles.kpiCard} onClick={() => setModalKpi({ tipo: 'clientes' })} style={{ cursor: 'pointer' }} title="Ver todos os clientes">
              <div className={styles.kpiLabel}>Total Clientes</div>
              <div className={styles.kpiValue}>{fmtNum(kpis.total_clientes)}</div>
              <div className={styles.kpiSub}>cadastrados</div>
            </div>
            <div className={styles.kpiCard} onClick={() => setModalKpi({ tipo: 'descontos' })} style={{ cursor: 'pointer' }} title="Ver pedidos com desconto">
              <div className={styles.kpiLabel}>Descontos Concedidos</div>
              <div className={styles.kpiValue}>{fmtBRL(kpis.total_descontos)}</div>
            </div>
          </div>

          <div className={styles.gridTwo}>
            <div className={styles.chartCard}>
              <h3>Faturamento por Dia <span style={{ fontSize: 12, fontWeight: 400, color: '#6c757d' }}>(últimos 20 dias)</span></h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={diarioGrafico}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E9ECEF" />
                  <XAxis dataKey="dia" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(v, name) => name === 'faturamento' ? fmtBRL(v) : fmtNum(v)}
                    labelFormatter={(d) => `Dia: ${d}`}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="faturamento" stroke="#6C5CE7" strokeWidth={2} name="Faturamento" />
                  <Line type="monotone" dataKey="pedidos" stroke="#2ECC71" strokeWidth={2} yAxisId="right" name="Pedidos" />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className={styles.chartCard}>
              <h3>
                Pedidos por Status
                <span style={{ fontSize: '0.75rem', fontWeight: 400, color: '#6C757D', marginLeft: 8 }}>
                  · mês atual
                </span>
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={porStatusMes}
                    dataKey="quantidade"
                    nameKey="situacao"
                    cx="50%" cy="50%"
                    outerRadius={100}
                    label={({ situacao, quantidade }) => `${quantidade}`}
                  >
                    {porStatusMes.map((entry, i) => (
                      <Cell key={i} fill={STATUS_COLORS[entry.situacao] || '#95A5A6'} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => fmtNum(v) + ' pedidos'} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className={styles.tableCard}>
            <h3>
              Top 10 Produtos Mais Vendidos
              <span style={{ fontSize: '0.75rem', fontWeight: 400, color: '#6C757D', marginLeft: 8 }}>
                · mês atual
              </span>
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
              gap: 12,
              padding: '0.5rem 0',
            }}>
              {topProdutosMes.length === 0 && (
                <div style={{ gridColumn: '1/-1', textAlign: 'center', color: '#94a3b8', padding: 24 }}>
                  Nenhum produto vendido no mês.
                </div>
              )}
              {topProdutosMes.map((p, i) => {
                const estoque = p.estoque;
                const semEstoque = estoque === 0;
                const estoqueBaixo = estoque > 0 && estoque <= 5;
                const corEstoque = semEstoque ? '#dc2626' : estoqueBaixo ? '#d97706' : '#16a34a';
                const bgEstoque  = semEstoque ? '#fef2f2' : estoqueBaixo ? '#fffbeb' : '#ecfdf5';
                return (
                  <div key={p.referencia || i} style={{
                    border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff',
                    overflow: 'hidden', display: 'flex', flexDirection: 'column',
                    transition: 'transform .15s, box-shadow .15s', cursor: 'default',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.08)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <div style={{ position: 'relative', paddingTop: '100%', background: '#f8fafc' }}>
                      <img
                        src={p.foto_url_thumb}
                        alt={p.produto_nome || p.referencia}
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', padding: 8 }}
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                      <div style={{
                        position: 'absolute', top: 6, left: 6,
                        background: '#6C5CE7', color: '#fff', fontSize: 11, fontWeight: 700,
                        padding: '2px 6px', borderRadius: 6,
                      }}>#{i + 1}</div>
                    </div>
                    <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                      <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{p.referencia}</div>
                      <div style={{ fontSize: 13, color: '#1f2937', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: 32 }}>
                        {p.produto_nome || p.descricao}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 'auto', paddingTop: 8 }}>
                        <div>
                          <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600 }}>Vendido</div>
                          <div style={{ fontSize: 18, fontWeight: 700, color: '#6C5CE7' }}>{fmtNum(p.total_vendido)}</div>
                        </div>
                        <div style={{
                          background: bgEstoque, color: corEstoque,
                          padding: '4px 8px', borderRadius: 6, fontWeight: 700, fontSize: 12,
                          textAlign: 'right',
                        }}>
                          <div style={{ fontSize: 9, opacity: 0.7 }}>ESTOQUE</div>
                          {estoque == null ? '—' : fmtNum(estoque)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {!loading && tab === 'pedidos' && (
        <div className={styles.tableCard}>
          <h3>
            Pedidos
            <span style={{ fontSize: '0.85rem', fontWeight: 400, color: '#6C757D', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span>{pedidosFiltered.length} de {pedidos.length} · {fmtBRL(pedidosFiltered.reduce((s, p) => s + Number(p.valor_total || 0), 0))}</span>
              {hasActiveFilter(pedidoFilters) && (
                <button className={styles.linkExpand} onClick={() => limparFiltros(pedidoFilters, setPedidoFilters)}>
                  Limpar filtros
                </button>
              )}
            </span>
          </h3>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <SortableTh sortKey="id" currentSort={pedidoSort} onSort={(k) => toggleSort(pedidoSort, setPedidoSort, k)}>#</SortableTh>
                  <SortableTh sortKey="data" currentSort={pedidoSort} onSort={(k) => toggleSort(pedidoSort, setPedidoSort, k)}>Data</SortableTh>
                  <SortableTh sortKey="cliente_nome" currentSort={pedidoSort} onSort={(k) => toggleSort(pedidoSort, setPedidoSort, k)}>Cliente</SortableTh>
                  <SortableTh sortKey="situacao" currentSort={pedidoSort} onSort={(k) => toggleSort(pedidoSort, setPedidoSort, k)}>Status</SortableTh>
                  <SortableTh sortKey="tipo_pagamento" currentSort={pedidoSort} onSort={(k) => toggleSort(pedidoSort, setPedidoSort, k)}>Pagamento</SortableTh>
                  <SortableTh sortKey="qtde_itens" currentSort={pedidoSort} onSort={(k) => toggleSort(pedidoSort, setPedidoSort, k)}>Itens</SortableTh>
                  <SortableTh sortKey="frete_descricao" currentSort={pedidoSort} onSort={(k) => toggleSort(pedidoSort, setPedidoSort, k)}>Frete</SortableTh>
                  <SortableTh sortKey="valor_total" currentSort={pedidoSort} onSort={(k) => toggleSort(pedidoSort, setPedidoSort, k)} align="right">Valor</SortableTh>
                </tr>
                <tr>
                  <th><ColumnFilter filter={pedidoFilters.id} onChange={updateFilter(pedidoFilters, setPedidoFilters, 'id')} /></th>
                  <th><ColumnFilter filter={pedidoFilters.data} onChange={updateFilter(pedidoFilters, setPedidoFilters, 'data')} /></th>
                  <th><ColumnFilter filter={pedidoFilters.cliente_nome} onChange={updateFilter(pedidoFilters, setPedidoFilters, 'cliente_nome')} /></th>
                  <th><ColumnFilter filter={pedidoFilters.situacao} onChange={updateFilter(pedidoFilters, setPedidoFilters, 'situacao')} options={pedidoStatusOptions} /></th>
                  <th><ColumnFilter filter={pedidoFilters.tipo_pagamento} onChange={updateFilter(pedidoFilters, setPedidoFilters, 'tipo_pagamento')} options={pedidoPagamentoOptions} /></th>
                  <th><ColumnFilter filter={pedidoFilters.qtde_itens} onChange={updateFilter(pedidoFilters, setPedidoFilters, 'qtde_itens')} /></th>
                  <th><ColumnFilter filter={pedidoFilters.frete_descricao} onChange={updateFilter(pedidoFilters, setPedidoFilters, 'frete_descricao')} options={pedidoFreteOptions} /></th>
                  <th><ColumnFilter filter={pedidoFilters.valor_total} onChange={updateFilter(pedidoFilters, setPedidoFilters, 'valor_total')} /></th>
                </tr>
              </thead>
              <tbody>
                {pedidosFiltered.length === 0 ? (
                  <tr><td colSpan={8} className={styles.empty}>
                    {pedidos.length === 0 ? 'Nenhum pedido no período' : 'Nenhum resultado com os filtros aplicados'}
                  </td></tr>
                ) : (
                  pedidosFiltered.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <button
                          type="button"
                          onClick={() => abrirPedido(p.id)}
                          title="Ver detalhes do pedido"
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            color: '#6C5CE7',
                            fontWeight: 700,
                            cursor: 'pointer',
                            textDecoration: 'underline'
                          }}
                        >
                          {p.id}
                        </button>
                      </td>
                      <td>{fmtDate(p.data)}</td>
                      <td style={{ fontSize: '0.8rem' }} title={p.cliente_email || p.id_usuario || ''}>
                        {p.cliente_nome || p.cliente_email || (p.id_usuario ? `${p.id_usuario.slice(0, 12)}…` : '—')}
                      </td>
                      <td><span className={`${styles.statusBadge} ${statusClass(p.situacao)}`}>{p.situacao}</span></td>
                      <td>{p.tipo_pagamento || '-'}</td>
                      <td>{p.qtde_itens}</td>
                      <td>{p.frete_descricao || '-'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtBRL(p.valor_total)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && tab === 'cestas' && (
        <div className={styles.tableCard}>
          <h3>
            Cestas Abandonadas
            <span style={{ fontSize: '0.85rem', fontWeight: 400, color: '#6C757D', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span>
                {cestasFiltered.length} de {cestas.length} · {fmtBRL(cestasFiltered.reduce((s, c) => s + c.valor_estimado, 0))} em potencial
              </span>
              {hasActiveFilter(cestaFilters) && (
                <button className={styles.linkExpand} onClick={() => limparFiltros(cestaFilters, setCestaFilters)}>
                  Limpar filtros
                </button>
              )}
            </span>
          </h3>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <SortableTh sortKey="nome" currentSort={cestaSort} onSort={(k) => toggleSort(cestaSort, setCestaSort, k)}>Cliente</SortableTh>
                  <SortableTh sortKey="email" currentSort={cestaSort} onSort={(k) => toggleSort(cestaSort, setCestaSort, k)}>E-mail</SortableTh>
                  <SortableTh sortKey="qtde_itens" currentSort={cestaSort} onSort={(k) => toggleSort(cestaSort, setCestaSort, k)}>Itens</SortableTh>
                  <SortableTh sortKey="dias_abandono" currentSort={cestaSort} onSort={(k) => toggleSort(cestaSort, setCestaSort, k)}>Dias abandonado</SortableTh>
                  <SortableTh sortKey="data_inclusao_mais_antiga" currentSort={cestaSort} onSort={(k) => toggleSort(cestaSort, setCestaSort, k)}>Início do abandono</SortableTh>
                  <SortableTh sortKey="valor_estimado" currentSort={cestaSort} onSort={(k) => toggleSort(cestaSort, setCestaSort, k)} align="right">Valor</SortableTh>
                  <th></th>
                </tr>
                <tr>
                  <th><ColumnFilter filter={cestaFilters.nome} onChange={updateFilter(cestaFilters, setCestaFilters, 'nome')} /></th>
                  <th><ColumnFilter filter={cestaFilters.email} onChange={updateFilter(cestaFilters, setCestaFilters, 'email')} /></th>
                  <th><ColumnFilter filter={cestaFilters.qtde_itens} onChange={updateFilter(cestaFilters, setCestaFilters, 'qtde_itens')} /></th>
                  <th><ColumnFilter filter={cestaFilters.dias_abandono} onChange={updateFilter(cestaFilters, setCestaFilters, 'dias_abandono')} /></th>
                  <th><ColumnFilter filter={cestaFilters.data_inclusao_mais_antiga} onChange={updateFilter(cestaFilters, setCestaFilters, 'data_inclusao_mais_antiga')} /></th>
                  <th><ColumnFilter filter={cestaFilters.valor_estimado} onChange={updateFilter(cestaFilters, setCestaFilters, 'valor_estimado')} /></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {cestasFiltered.length === 0 ? (
                  <tr><td colSpan={7} className={styles.empty}>
                    {cestas.length === 0 ? 'Nenhuma cesta abandonada' : 'Nenhum resultado com os filtros aplicados'}
                  </td></tr>
                ) : (
                  cestasFiltered.map((c) => {
                    const dias = c.dias_abandono ?? 0;
                    const diasClass = dias >= 14 ? styles.hot : dias >= 7 ? styles.warm : styles.cold;
                    return (
                      <tr key={c.id_usuario}>
                        <td>{c.nome || <em style={{ color: '#ADB5BD' }}>Sem cadastro</em>}</td>
                        <td style={{ fontSize: '0.8rem' }}>{c.email || '-'}</td>
                        <td>{c.qtde_itens}</td>
                        <td>
                          <span className={`${styles.diasBadge} ${diasClass}`}>
                            {dias === 0 ? 'hoje' : `${dias} ${dias === 1 ? 'dia' : 'dias'}`}
                          </span>
                        </td>
                        <td style={{ fontSize: '0.8rem' }}>
                          {c.data_inclusao_mais_antiga
                            ? fmtDate(c.data_inclusao_mais_antiga.replace(' ', 'T'))
                            : '-'}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtBRL(c.valor_estimado)}</td>
                        <td>
                          <button className={styles.linkExpand} onClick={() => abrirItensCesta(c.id_usuario)}>
                            Ver itens
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modalCesta && (
        <div className={styles.modalBackdrop} onClick={() => setModalCesta(null)}>
          <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <h2>{modalCesta.cliente?.nome || 'Cliente sem cadastro'}</h2>
                <p>
                  {modalCesta.cliente?.email || '—'}
                  {modalCesta.cliente?.perfil && <> · {modalCesta.cliente.perfil}</>}
                </p>
              </div>
              <button className={styles.modalClose} onClick={() => setModalCesta(null)}>×</button>
            </div>

            {modalCesta.loading && <div className={styles.loading}>Carregando itens...</div>}

            {!modalCesta.loading && modalCesta.cesta && (
              <>
                <div className={styles.modalStats}>
                  <div className={styles.modalStat}>
                    <div className={styles.modalStatLabel}>Itens</div>
                    <div className={styles.modalStatValue}>{modalCesta.cesta.qtde_itens}</div>
                  </div>
                  <div className={styles.modalStat}>
                    <div className={styles.modalStatLabel}>Dias abandonado</div>
                    <div className={styles.modalStatValue}>
                      {modalCesta.cesta.dias_abandono === 0 ? 'hoje' : `${modalCesta.cesta.dias_abandono}`}
                    </div>
                  </div>
                  <div className={styles.modalStat}>
                    <div className={styles.modalStatLabel}>Início</div>
                    <div className={styles.modalStatValue} style={{ fontSize: '0.9rem' }}>
                      {modalCesta.cesta.data_inclusao_mais_antiga
                        ? fmtDate(modalCesta.cesta.data_inclusao_mais_antiga.replace(' ', 'T'))
                        : '-'}
                    </div>
                  </div>
                  <div className={styles.modalStat}>
                    <div className={styles.modalStatLabel}>Valor estimado</div>
                    <div className={styles.modalStatValue}>{fmtBRL(modalCesta.cesta.valor_estimado)}</div>
                  </div>
                </div>

                <div className={styles.modalBody}>
                  <div className={styles.itemGrid}>
                    {modalCesta.itens.map((it, i) => (
                      <div key={i} className={styles.itemCard}>
                        {(it.imagem_thumb || it.imagem_url) ? (
                          <img
                            className={styles.itemImg}
                            src={it.imagem_thumb || it.imagem_url}
                            alt={it.nome}
                            loading="lazy"
                            onError={(e) => {
                              // Se thumb falhar, tenta full URL
                              if (it.imagem_url && e.currentTarget.src !== it.imagem_url) {
                                e.currentTarget.src = it.imagem_url;
                              } else {
                                e.currentTarget.style.display = 'none';
                                const ph = e.currentTarget.nextElementSibling;
                                if (ph) ph.style.display = 'flex';
                              }
                            }}
                          />
                        ) : null}
                        <div
                          className={styles.itemImgPlaceholder}
                          style={{ display: (it.imagem_thumb || it.imagem_url) ? 'none' : 'flex' }}
                        >
                          Sem imagem
                        </div>
                        <div className={styles.itemInfo}>
                          {it.referencia && <div className={styles.itemRef}>{it.referencia}</div>}
                          <div className={styles.itemNome}>{it.nome}</div>
                          <div className={styles.itemMeta}>
                            {it.categoria && <span>📦 {it.categoria}</span>}
                            {it.fornecedor && <span>🏭 {it.fornecedor}</span>}
                            {it.estoque !== null && (
                              <span>
                                Est:{' '}
                                <span className={it.estoque <= 0 ? styles.estoqueRuim : styles.estoqueOk}>
                                  {it.estoque}
                                </span>
                              </span>
                            )}
                          </div>
                          <div className={styles.itemFooter}>
                            <div className={styles.itemPreco}>{fmtBRL(it.preco_unitario)}</div>
                            <div className={styles.itemQtde}>
                              × {it.qtde} = <strong>{fmtBRL(it.preco_total)}</strong>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {!modalCesta.loading && modalCesta.error && (
              <div className={styles.empty}>Erro: {modalCesta.error}</div>
            )}
          </div>
        </div>
      )}

      {modalPedido && (
        <div className={styles.modalBackdrop} onClick={() => setModalPedido(null)}>
          <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <h2>Pedido #{modalPedido.pedido?.id || '—'}</h2>
                <p>
                  {modalPedido.cliente?.nome || modalPedido.pedido?.id_usuario?.slice(0, 16) || 'Cliente sem cadastro'}
                  {modalPedido.cliente?.email && <> · {modalPedido.cliente.email}</>}
                </p>
              </div>
              <button className={styles.modalClose} onClick={() => setModalPedido(null)}>×</button>
            </div>

            {modalPedido.loading && <div className={styles.loading}>Carregando pedido...</div>}

            {!modalPedido.loading && modalPedido.pedido && (
              <>
                <div className={styles.modalStats}>
                  <div className={styles.modalStat}>
                    <div className={styles.modalStatLabel}>Data</div>
                    <div className={styles.modalStatValue} style={{ fontSize: '0.9rem' }}>
                      {modalPedido.pedido.data ? fmtDate(modalPedido.pedido.data) : '-'}
                    </div>
                  </div>
                  <div className={styles.modalStat}>
                    <div className={styles.modalStatLabel}>Status</div>
                    <div className={styles.modalStatValue} style={{ fontSize: '0.9rem' }}>
                      <span className={`${styles.statusBadge} ${statusClass(modalPedido.pedido.situacao)}`}>
                        {modalPedido.pedido.situacao || '-'}
                      </span>
                    </div>
                  </div>
                  <div className={styles.modalStat}>
                    <div className={styles.modalStatLabel}>Pagamento</div>
                    <div className={styles.modalStatValue} style={{ fontSize: '0.9rem' }}>
                      {modalPedido.pedido.tipo_pagamento || '-'}
                    </div>
                  </div>
                  <div className={styles.modalStat}>
                    <div className={styles.modalStatLabel}>Frete</div>
                    <div className={styles.modalStatValue} style={{ fontSize: '0.9rem' }}>
                      {modalPedido.pedido.frete_descricao || '-'}
                    </div>
                  </div>
                  <div className={styles.modalStat}>
                    <div className={styles.modalStatLabel}>Itens</div>
                    <div className={styles.modalStatValue}>{modalPedido.itens?.length || 0}</div>
                  </div>
                  <div className={styles.modalStat}>
                    <div className={styles.modalStatLabel}>Total</div>
                    <div className={styles.modalStatValue}>{fmtBRL(modalPedido.pedido.valor_total)}</div>
                  </div>
                </div>

                <div className={styles.modalBody}>
                  {modalPedido.itens && modalPedido.itens.length > 0 ? (
                    <div className={styles.itemGrid}>
                      {modalPedido.itens.map((it, i) => (
                        <div key={i} className={styles.itemCard}>
                          {it.imagem_url ? (
                            <img
                              className={styles.itemImg}
                              src={it.imagem_url}
                              alt={it.produto_nome || it.referencia}
                              loading="lazy"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                const ph = e.currentTarget.nextElementSibling;
                                if (ph) ph.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div
                            className={styles.itemImgPlaceholder}
                            style={{ display: it.imagem_url ? 'none' : 'flex' }}
                          >
                            Sem imagem
                          </div>
                          <div className={styles.itemInfo}>
                            {it.referencia && <div className={styles.itemRef}>{it.referencia}</div>}
                            <div className={styles.itemNome}>{it.produto_nome || it.descricao || '—'}</div>
                            <div className={styles.itemMeta}>
                              {it.categoria && <span>📦 {it.categoria}</span>}
                            </div>
                            <div className={styles.itemFooter}>
                              <div className={styles.itemPreco}>{fmtBRL(it.preco_unitario || it.preco)}</div>
                              <div className={styles.itemQtde}>
                                × {it.quantidade || it.qtde} = <strong>{fmtBRL((it.quantidade || it.qtde) * (it.preco_unitario || it.preco || 0))}</strong>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.empty}>Sem itens registrados pra esse pedido</div>
                  )}
                </div>
              </>
            )}

            {!modalPedido.loading && modalPedido.error && (
              <div className={styles.empty}>Erro: {modalPedido.error}</div>
            )}
          </div>
        </div>
      )}

      {modalKpi && kpis && (() => {
        const tipo = modalKpi.tipo;
        const titulos = {
          faturamento: 'Faturamento por Dia',
          ticket_medio: 'Ticket Médio por Dia',
          faturamento_dia: faturamentoDoDia.ehUmDia
            ? `Pedidos do dia ${fmtDate(faturamentoDoDia.dia)}`
            : `Pedidos do período ${fmtDate(faturamentoDoDia.periodoInicio)} a ${fmtDate(faturamentoDoDia.periodoFim)}`,
          cancelados: 'Pedidos Cancelados',
          novos_clientes: 'Novos Clientes',
          cestas: 'Cestas Abandonadas',
          clientes: 'Clientes Cadastrados',
          descontos: 'Pedidos com Desconto',
        };
        const subtitulos = {
          faturamento: `${fmtBRL(kpis.faturamento)} · ${fmtNum(kpis.total_pedidos)} pedidos no período`,
          ticket_medio: `Ticket médio do período: ${fmtBRL(kpis.ticket_medio)}`,
          faturamento_dia: `${fmtBRL(faturamentoDoDia.valor)} · ${fmtNum(faturamentoDoDia.pedidos)} pedidos`,
          cancelados: `${fmtNum(kpis.cancelados)} cancelados no período`,
          novos_clientes: `${fmtNum(kpis.novos_clientes)} clientes com 1º pedido no período`,
          cestas: `${fmtNum(kpis.cestas_abandonadas)} cestas · ${fmtBRL(kpis.valor_cestas)} em aberto`,
          clientes: `${fmtNum(kpis.total_clientes)} cadastrados`,
          descontos: `${fmtBRL(kpis.total_descontos)} concedidos no período`,
        };

        return (
          <div className={styles.modalBackdrop} onClick={() => setModalKpi(null)}>
            <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <div>
                  <h2>{titulos[tipo]}</h2>
                  <p>{subtitulos[tipo]}</p>
                </div>
                <button className={styles.modalClose} onClick={() => setModalKpi(null)}>×</button>
              </div>

              <div className={styles.modalBody}>
                {(tipo === 'faturamento' || tipo === 'ticket_medio') && (
                  <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>Data</th>
                          <th style={{ textAlign: 'right' }}>Pedidos</th>
                          <th style={{ textAlign: 'right' }}>Faturamento</th>
                          <th style={{ textAlign: 'right' }}>Ticket Médio</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...diario].reverse().map((d, i) => {
                          const ticket = (d.pedidos > 0) ? Number(d.faturamento) / Number(d.pedidos) : 0;
                          return (
                            <tr key={i}>
                              <td>{fmtDate(d.dia)}</td>
                              <td style={{ textAlign: 'right' }}>{fmtNum(d.pedidos)}</td>
                              <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtBRL(d.faturamento)}</td>
                              <td style={{ textAlign: 'right' }}>{fmtBRL(ticket)}</td>
                            </tr>
                          );
                        })}
                        <tr style={{ background: '#F8F9FA', fontWeight: 700, borderTop: '2px solid #495057' }}>
                          <td>TOTAL</td>
                          <td style={{ textAlign: 'right' }}>{fmtNum(kpis.total_pedidos)}</td>
                          <td style={{ textAlign: 'right' }}>{fmtBRL(kpis.faturamento)}</td>
                          <td style={{ textAlign: 'right' }}>{fmtBRL(kpis.ticket_medio)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {(tipo === 'faturamento_dia' || tipo === 'cancelados' || tipo === 'descontos') && (() => {
                  let lista = pedidos;
                  if (tipo === 'faturamento_dia') {
                    if (faturamentoDoDia.ehUmDia) {
                      lista = pedidos.filter(p => (p.data || '').slice(0, 10) === faturamentoDoDia.dia);
                    }
                    // Quando é período, mostra todos os pedidos (que já vêm filtrados pelo intervalo no backend)
                  } else if (tipo === 'cancelados') {
                    lista = pedidos.filter(p => p.situacao === 'Cancelado');
                  } else if (tipo === 'descontos') {
                    lista = pedidos.filter(p => Number(p.valor_descontos || 0) > 0)
                      .sort((a, b) => Number(b.valor_descontos) - Number(a.valor_descontos));
                  }
                  if (lista.length === 0) return <div className={styles.empty}>Nenhum pedido para exibir.</div>;
                  return (
                    <div className={styles.tableWrapper}>
                      <table className={styles.table}>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Data</th>
                            <th>Status</th>
                            <th>Pagamento</th>
                            <th style={{ textAlign: 'right' }}>Itens</th>
                            {tipo === 'descontos' && <th style={{ textAlign: 'right' }}>Desconto</th>}
                            <th style={{ textAlign: 'right' }}>Valor</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lista.map(p => (
                            <tr key={p.id}>
                              <td>
                                <button
                                  type="button"
                                  onClick={() => { setModalKpi(null); abrirPedido(p.id); }}
                                  style={{ background: 'none', border: 'none', padding: 0, color: '#6C5CE7', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}
                                >
                                  {p.id}
                                </button>
                              </td>
                              <td>{fmtDate(p.data)}</td>
                              <td><span className={`${styles.statusBadge} ${statusClass(p.situacao)}`}>{p.situacao}</span></td>
                              <td>{p.tipo_pagamento || '-'}</td>
                              <td style={{ textAlign: 'right' }}>{p.qtde_itens}</td>
                              {tipo === 'descontos' && <td style={{ textAlign: 'right', color: '#E74C3C' }}>{fmtBRL(p.valor_descontos)}</td>}
                              <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtBRL(p.valor_total)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}

                {tipo === 'novos_clientes' && (() => {
                  // Heurística: clientes da lista cuja data de criação cai no período.
                  const lista = clientes.filter(c => {
                    if (!c.criado_em) return false;
                    const d = c.criado_em.slice(0, 10);
                    return d >= dataInicio && d <= dataFim;
                  });
                  if (lista.length === 0) return (
                    <div className={styles.empty}>
                      Lista detalhada não está em cache. Veja a aba <strong>Clientes</strong> pra ver todos.
                    </div>
                  );
                  return (
                    <div className={styles.tableWrapper}>
                      <table className={styles.table}>
                        <thead>
                          <tr><th>Nome</th><th>E-mail</th><th>Perfil</th><th>Criado em</th></tr>
                        </thead>
                        <tbody>
                          {lista.map((c, i) => (
                            <tr key={i}>
                              <td>{c.nome || '—'}</td>
                              <td>{c.email || '—'}</td>
                              <td>{c.perfil || '—'}</td>
                              <td>{c.criado_em ? fmtDate(c.criado_em.replace(' ', 'T')) : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}

                {tipo === 'cestas' && (() => {
                  if (cestas.length === 0) return <div className={styles.empty}>Nenhuma cesta abandonada.</div>;
                  const ordenadas = [...cestas].sort((a, b) => Number(b.valor_estimado || 0) - Number(a.valor_estimado || 0));
                  return (
                    <div className={styles.tableWrapper}>
                      <table className={styles.table}>
                        <thead>
                          <tr>
                            <th>Cliente</th>
                            <th>E-mail</th>
                            <th style={{ textAlign: 'right' }}>Itens</th>
                            <th style={{ textAlign: 'right' }}>Dias</th>
                            <th style={{ textAlign: 'right' }}>Valor</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ordenadas.map((c, i) => (
                            <tr
                              key={i}
                              style={{ cursor: 'pointer' }}
                              onClick={() => { setModalKpi(null); abrirItensCesta(c.id_usuario); }}
                              title="Ver itens da cesta"
                            >
                              <td>{c.nome || '—'}</td>
                              <td>{c.email || '—'}</td>
                              <td style={{ textAlign: 'right' }}>{c.qtde_itens}</td>
                              <td style={{ textAlign: 'right' }}>{c.dias_abandono}</td>
                              <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtBRL(c.valor_estimado)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}

                {tipo === 'clientes' && (() => {
                  if (clientes.length === 0) return <div className={styles.empty}>Sem clientes carregados. Vá pra aba Clientes.</div>;
                  return (
                    <div className={styles.tableWrapper}>
                      <table className={styles.table}>
                        <thead>
                          <tr><th>Nome</th><th>E-mail</th><th>Perfil</th><th>CPF/CNPJ</th></tr>
                        </thead>
                        <tbody>
                          {clientes.slice(0, 200).map((c, i) => (
                            <tr key={i}>
                              <td>{c.nome || '—'}</td>
                              <td>{c.email || '—'}</td>
                              <td>{c.perfil || '—'}</td>
                              <td>{c.cpf || c.cnpj || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {clientes.length > 200 && (
                        <div className={styles.empty}>Mostrando 200 de {fmtNum(clientes.length)}. Use a aba Clientes pra filtrar.</div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        );
      })()}

      {!loading && tab === 'clientes' && (
        <div className={styles.tableCard}>
          <h3>
            Clientes Cadastrados
            <span style={{ fontSize: '0.85rem', fontWeight: 400, color: '#6C757D', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span>{clientesFiltered.length} de {clientes.length}</span>
              {hasActiveFilter(clienteFilters) && (
                <button className={styles.linkExpand} onClick={() => limparFiltros(clienteFilters, setClienteFilters)}>
                  Limpar filtros
                </button>
              )}
            </span>
          </h3>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <SortableTh sortKey="nome" currentSort={clienteSort} onSort={(k) => toggleSort(clienteSort, setClienteSort, k)}>Nome</SortableTh>
                  <SortableTh sortKey="email" currentSort={clienteSort} onSort={(k) => toggleSort(clienteSort, setClienteSort, k)}>E-mail</SortableTh>
                  <SortableTh sortKey="documento" currentSort={clienteSort} onSort={(k) => toggleSort(clienteSort, setClienteSort, k)}>CPF/CNPJ</SortableTh>
                  <SortableTh sortKey="perfil" currentSort={clienteSort} onSort={(k) => toggleSort(clienteSort, setClienteSort, k)}>Perfil</SortableTh>
                  <SortableTh sortKey="minimo_valor" currentSort={clienteSort} onSort={(k) => toggleSort(clienteSort, setClienteSort, k)} align="right">Mín. Compra</SortableTh>
                  <SortableTh sortKey="desconto" currentSort={clienteSort} onSort={(k) => toggleSort(clienteSort, setClienteSort, k)} align="right">Desconto</SortableTh>
                </tr>
                <tr>
                  <th><ColumnFilter filter={clienteFilters.nome} onChange={updateFilter(clienteFilters, setClienteFilters, 'nome')} /></th>
                  <th><ColumnFilter filter={clienteFilters.email} onChange={updateFilter(clienteFilters, setClienteFilters, 'email')} /></th>
                  <th><ColumnFilter filter={clienteFilters.documento} onChange={updateFilter(clienteFilters, setClienteFilters, 'documento')} /></th>
                  <th><ColumnFilter filter={clienteFilters.perfil} onChange={updateFilter(clienteFilters, setClienteFilters, 'perfil')} options={clientePerfilOptions} /></th>
                  <th><ColumnFilter filter={clienteFilters.minimo_valor} onChange={updateFilter(clienteFilters, setClienteFilters, 'minimo_valor')} /></th>
                  <th><ColumnFilter filter={clienteFilters.desconto} onChange={updateFilter(clienteFilters, setClienteFilters, 'desconto')} /></th>
                </tr>
              </thead>
              <tbody>
                {clientesFiltered.length === 0 ? (
                  <tr><td colSpan={6} className={styles.empty}>
                    {clientes.length === 0 ? 'Nenhum cliente cadastrado' : 'Nenhum resultado com os filtros aplicados'}
                  </td></tr>
                ) : (
                  clientesFiltered.map((c) => (
                    <tr key={c.id}>
                      <td>{c.nome || '-'}</td>
                      <td style={{ fontSize: '0.8rem' }}>{c.email || '-'}</td>
                      <td>{c.documento || '-'}</td>
                      <td>{c.perfil || '-'}</td>
                      <td style={{ textAlign: 'right' }}>{fmtBRL(c.minimo_valor)}</td>
                      <td style={{ textAlign: 'right' }}>{Number(c.desconto || 0).toFixed(2)}%</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'recorrencia' && (<RecorrenciaTab />)}
    </div>
  );
};

export default Macro;
